import { DotsThreeVertical } from "@phosphor-icons/react";
import { useMemo } from "react";

import DesktopActions from "common/components/DesktopActions";
import MemberPermissionsTag from "common/components/MemberPermissionsTag";
import SimpleDataTable from "common/components/SimpleDataTable";
import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import { Button } from "common/components/ui/Button";
import type { Member } from "common/types";

import { CLIENT_ACTIONS } from "../../../constants";
import { formatBillingDate } from "../../../utils";
import ClientActions from "../ClientActions";

import type { ColumnDef } from "@tanstack/react-table";

export default function DesktopTable({ clients }: { clients: Member[] }) {
  const columns = useMemo<ColumnDef<Member>[]>(
    () => [
      {
        id: "client",
        header: "Client",
        cell: ({ row }) => {
          const {
            first_name: firstName,
            last_name: lastName,
            email,
          } = row.original;

          return (
            <div className="flex items-center gap-2.5">
              <AimfoxAvatar firstName={firstName} lastName={lastName} />

              <div className="flex flex-col items-start gap-1">
                <span className="text-md-bold text-black-950">
                  {firstName} {lastName}
                </span>

                <span className="inline-block max-w-[40ch] overflow-hidden text-ellipsis text-xs-medium text-black-500">
                  {email}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "role",
        header: "Role",
        cell: ({ row }) => (
          <MemberPermissionsTag
            permissions={row.original.permissions}
            role={row.original.role}
          />
        ),
      },
      {
        id: "dateAdded",
        header: "Date Added",
        cell: ({ row }) => (
          <span className="text-button-xs text-black-950">
            {formatBillingDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <DesktopActions
            triggerElement={
              <Button intent="iconOnly" variant="secondary-black" size="sm">
                <DotsThreeVertical />
              </Button>
            }
            editOptions={CLIENT_ACTIONS}
          >
            {(action, onClose) => (
              <ClientActions
                action={action}
                member={row.original}
                onClose={onClose}
                onBack={onClose}
              />
            )}
          </DesktopActions>
        ),
      },
    ],
    [],
  );

  return (
    <SimpleDataTable
      columns={columns}
      data={clients}
      localStorageId="clients"
    />
  );
}
